import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Observer, Subject, Subscription, tap, map, filter, takeUntil } from 'rxjs';
import { CompanySettings, ICompany } from '../_interface/company';
import { IModifier } from '../_interface/modifier';
import { Group, Product } from '../_interface/product';
import { CompanyService } from './company.service';
import { GroupService } from './group.service';
import { ModifierService } from './modifier.service';
import { ProductService } from './product.service';
import { WebsocketData, WSService } from './ws.service';
import { TookanService } from '../tookan/_service/tookan.service';
import { TableService } from './table.service';
import { Table } from '../_interface/table';
import { AuthorizationService } from './authorization.service';
import { Permissions } from 'src/app/core/_interface/permissions';
import { PriceList } from '../_interface/pricelist';
import { PriceListService } from './price-list.service';
import { environment } from 'src/environments/environment';
import { ApiResponse } from '../_interface/apiResponse';
import { HttpClient } from '@angular/common/http';
import { NGXLogger } from 'ngx-logger';
import { logRetry, retryTransient } from '../_helpers/retry-transient';

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private _userPermissions: BehaviorSubject<Permissions[]> | null = null;
  private _organizationPermissions: BehaviorSubject<Permissions[] | null> | null = null;

  wsConnectionInitialized = new BehaviorSubject(false);

  selectedCompany = new BehaviorSubject<ICompany | null>(null);
  _products: BehaviorSubject<Product[]> | null = null;
  /** Flips to true once products have first loaded (success or error). Views can wait
   *  on this before rendering product-dependent, filtered lists to avoid a flash of
   *  unfiltered data while products are still loading. */
  productsLoaded$ = new BehaviorSubject<boolean>(false);
  _modifiers: BehaviorSubject<IModifier[]> | null = null;
  _priceLists: BehaviorSubject<PriceList[]> | null = null;
  _tables?: BehaviorSubject<Table[]>;
  /** Flips to true once tables have first loaded (success or error). Bez ovoga se objekat BEZ
   *  stolova ne razlikuje od „još učitavam" — `_tables` startuje kao prazan niz, pa je ekran
   *  stolova zauvek stajao na „Učitavam stolove". Isti obrazac kao `productsLoaded$`. */
  tablesLoaded$ = new BehaviorSubject<boolean>(false);
  /** Poslednje učitavanje stolova nije uspelo (nema prava, greška) — prazna lista tada NE znači
   *  da objekat nema stolove, pa prikaz mora da poruči nešto drugo. */
  tablesLoadFailed$ = new BehaviorSubject<boolean>(false);

  private _companySettings: BehaviorSubject<CompanySettings | null> | null = null;

  /**
   * Lista kodova aktivnih Billing servisa za trenutno selektovanu firmu.
   * Populates: (1) na promenu `selectedCompany` se učita kroz `BillingService.getActiveServiceCodes`,
   * (2) na WS `billingServicesChanged` poruku se push-uje sveža lista bez REST poziva.
   *
   * Template usage: `(session.activeServiceCodes$ | async)?.includes('WOLT')`.
   * Imperative usage: `session.hasService('WOLT')`.
   */
  private _activeServiceCodes = new BehaviorSubject<string[]>([]);
  get activeServiceCodes$(): Observable<string[]> { return this._activeServiceCodes.asObservable(); }
  hasService(code: string): boolean { return this._activeServiceCodes.value.includes(code); }

  private _groups: BehaviorSubject<Group[]> | null = null;

  /** Učitavanja u toku, po listi — i ona koja čekaju ponovni pokušaj. Vidi `loadWithRetry`. */
  private loads = new Map<string, Subscription>();
  /** Učitavanje stolova u toku — vodi se odvojeno od `loads`, vidi `loadTables`. */
  private tablesLoad?: Subscription;

  constructor(
    private modifierService: ModifierService,
    private groupService: GroupService,
    private productService: ProductService,
    private companyService: CompanyService,
    private tableService: TableService,
    private priceService: PriceListService,
    private ws: WSService,
    private authService: AuthorizationService,
    private tookan: TookanService,
    private http: HttpClient,
    private logger: NGXLogger
  ) {

    productService.productSaved.subscribe({ next: p => this.onProductSaved(p) })
    productService.productsDeleted.subscribe({ next: p => this.onProductsDeleted(p) })

    groupService.groupSaved.subscribe({ next: g => this.onGroupSaved(g) })
    groupService.groupDeleted.subscribe({ next: p => this.onGroupDeleted(p) })

    modifierService.modifierSaved.subscribe({ next: s => this.onModifierSaved(s) })
    modifierService.modifierDeleted.subscribe({ next: s => this.onModifierDeleted(s) })

    companyService.companySettingsSaved.subscribe({ next: g => this.onCompanySettingsSaved(g) })

    ws.authenticated.subscribe(
      authenticated => {
        if (authenticated) {
          this.sendWSCompanyID(this.selectedCompany.value);
        }
      }
    );

    ws.observe('companySettingsChanged').subscribe(
      {
        next: (msg) => this.onCompanySettingsPushed(msg.data.settings)
      }
    );

    // Push-only osvežavanje aktivnih billing servisa — server pošalje kad admin
    // aktivira/deaktivira paket (BillingService.SubscriptionsChanged → WS).
    ws.observe('billing.servicesChanged').subscribe({
      next: (msg) => {
        const codes = msg?.data?.activeServiceCodes ?? [];
        this._activeServiceCodes.next(codes);
      }
    });

    this.selectedCompany.subscribe(
      company => {
        this.clear();
        if (company) {
          this.logger.log("company changed to " + company.companyID);
          this.load();
          this.sendWSCompanyID(company);
          this.loadActiveServiceCodes(company.companyID);
        }

      }
    )

    this.authService.currentUser.subscribe(() => {
      this.selectedCompany.next(null);
    }
    );

    // Tookan API key se setuje kombinacijom companySettings.tookan_Enabled (operator switch)
    // i Billing TOOKAN subscription-a. Oba moraju biti true. Reagujemo na promenu bilo kog izvora.
    const applyTookan = () => {
      const settings = this._companySettings?.value;
      if (settings?.tookan_Enabled && this.hasService('TOOKAN')) {
        tookan.setApiKey(settings.tookan_Key || "");
      } else {
        tookan.setApiKey("");
      }
    };
    this.companySettings.subscribe({ next: () => applyTookan() });
    this._activeServiceCodes.subscribe({ next: () => applyTookan() });

  }

  private loadActiveServiceCodes(companyID: number) {
    // Direktan HTTP poziv umesto admin BillingService-a — core je deljen sa app-ovima koji
    // nemaju admin projekat (konobar, KD, kiosk…), pa ne sme da importuje iz src/app/admin.
    const url = `${environment.apiBase}billing/activeServiceCodes?companyID=${companyID}`;
    this.loadWithRetry('active service codes', this.http.get<string[]>(url), {
      next: codes => this._activeServiceCodes.next(codes ?? []),
      error: err => this.logger.warn('Failed to load active service codes', err)
    });
  }

  /**
   * Pokreće učitavanje liste za tekući lokal i ponavlja ga dok ne uspe (v. `retryTransient`).
   *
   * Ove liste se traže JEDNOM po izboru lokala. Kad taj jedan zahtev padne — čvor iza gateway-a
   * se baš gasi, mreža trepne — lista bi ostala prazna do sledećeg reloada strane (KD je tako
   * prikazivao porudžbine bez naziva modifikatora).
   *
   * Novo učitavanje iste liste i `clear()` (promena lokala, odjava) prekidaju prethodno, i dok
   * čeka ponovni pokušaj — da zakasneli odgovor za stari lokal ne pregazi podatke novog.
   */
  private loadWithRetry<T>(name: string, source: Observable<T>, observer: Partial<Observer<T>>) {
    this.loads.get(name)?.unsubscribe();

    this.loads.set(name, source.pipe(
      retryTransient(logRetry(this.logger, `Loading ${name}`))
    ).subscribe(observer));
  }


  get tables(): BehaviorSubject<Table[]> {
    if (!this._tables) {
      this._tables = new BehaviorSubject<Table[]>([]);
      this.loadTables();
    }

    return this._tables;
  }

  /**
   * Stolovi se, kao i liste iz `load()`, traže jednom pa se ponavljaju dok zahtev ne uspe.
   *
   * `tablesLoaded$` se postavlja tek kad je ishod poznat — dok traju ponovni pokušaji lista se
   * još učitava. Konobar na „učitano, a nema stolova" prelazi na direktne porudžbine i taj izbor
   * pamti, pa bi mu jedan neuspeo zahtev pri startu trajno promenio prikaz.
   *
   * Stolove ne traži `load()` nego aplikacija, pa ih `clear()` ne prekida: ponovni izbor ISTOG
   * lokala bi prekinuo učitavanje koje niko ne pokreće ponovo. Prekida ga novi poziv i odlazak sa
   * lokala za koji je krenulo (drugi lokal, odjava).
   */
  loadTables() {
    const companyID = this.companyID;
    if (!companyID) {
      return;
    }

    this.tablesLoad?.unsubscribe();

    this.tablesLoad = this.tableService.get(companyID).pipe(
      retryTransient(logRetry(this.logger, 'Loading tables')),
      takeUntil(this.selectedCompany.pipe(filter(company => company?.companyID !== companyID)))
    ).subscribe({
      next: (data) => {
        // `TableService.get` na neuspeh (npr. AccessDenied) vraća undefined, a subject je
        // `BehaviorSubject<Table[]>` — bez `?? []` bi `session.tables.value` postao undefined i
        // svaki `.length`/`.filter` nad njim bi pukao (ekran stolova je tako ostajao prazan).
        this._tables?.next((data ?? []) as Table[]);
        this.tablesLoadFailed$.next(data === undefined);
        this.tablesLoaded$.next(true);
      },
      error: () => {
        this.tablesLoadFailed$.next(true);
        this.tablesLoaded$.next(true);
      }
    });
  }

  load() {
    if (this._groups) this.loadGroups();
    if (this._products) this.loadProducts();
    if (this._modifiers) this.loadModifiers();
    if (this._priceLists) this.loadPriceLists();
    if (this._companySettings) this.loadCompanySettings();
    // Dozvole su PO LOKALU — bez ovoga bi posle promene lokala ostale one od prethodnog
    // (a od popravke `clear()`-a prazne, jer subject više ne umire i ne pravi se nanovo).
    // Jedan poziv puni oba nivoa, pa je dovoljno da je bilo koji od njih tražen.
    if (this._userPermissions || this._organizationPermissions) this.loadUserPermissions();
  }

  onProductsDeleted(ids: number[]): void {
    var newList = this._products?.value.filter(p => !ids.some(pid => pid == p.productID));
    if (newList) {
      this._products?.next(newList);
    }
  }

  onGroupDeleted(id: number): void {
    var newList = this._groups?.value.filter(p => p.groupID != id);
    if (newList) {
      this._groups?.next(newList);
    }
  }

  /**
   * Dozvole tekućeg korisnika NA tekućem lokalu.
   *
   * ⚠ Instanca subject-a je STABILNA za ceo život servisa — `clear()` je samo isprazni, kao i
   * ostale lazy liste ovde. Ranije ju je `clear()` postavljao na `null`, pa je sledeći pristup
   * pravio NOV subject: ko je stari uhvatio jednom (`combineLatest([…, session.userPermissions])`
   * u konstruktoru, ili zapamćen u polju) zauvek je slušao odbačen subject i ostajao na `[]` —
   * UI iza dozvole se tiho ponašao kao da korisnik nema nijednu. Šabloni sa `| async` to nisu
   * osećali jer getter čitaju na svaku proveru promena, pa uvek dobiju tekuću instancu.
   */
  get userPermissions(): BehaviorSubject<Permissions[]> {
    if (!this._userPermissions) {
      this._userPermissions = new BehaviorSubject<Permissions[]>([]);
      this.loadUserPermissions();
    }

    return this._userPermissions;
  }

  /**
   * Prava tekućeg korisnika na nivou ORGANIZACIJE kojoj tekući lokal pripada.
   *
   * Postoji zato što `userPermissions` nosi isključivo prava nad LOKALOM, a server te dve provere
   * ne izjednačava: `checkOrganizationPermissions` ne gleda prava nad lokalom (obrnuto gleda).
   * Bez ovoga klijent ne može da zna sme li korisnik da menja ono što je zajedničko za sve lokale
   * — npr. meni firme.
   *
   * Pune se ISTIM pozivom (`loadUserPermissions`), pa oba nivoa uvek opisuju isti lokal; dva
   * odvojena poziva su mogla da se raziđu pri brzoj promeni lokala.
   *
   * `[]` = nema nijedno org pravo (ili lokal nije u organizaciji).
   * `null` = odgovor nije stigao (stariji server, greška) — pozivalac tada odlučuje po pravu nad
   * lokalom, umesto da sakrije ekran zbog greške u mreži.
   */
  get organizationPermissions(): BehaviorSubject<Permissions[] | null> {
    if (!this._organizationPermissions) {
      this._organizationPermissions = new BehaviorSubject<Permissions[] | null>(null);
      this.loadUserPermissions();
    }

    return this._organizationPermissions;
  }

  /**
   * Dovlači dozvole za tekući lokal. Bez lokala ili bez korisnika nema šta da se pita, pa lista
   * ostaje prazna — ali se poziva i iz `load()`, pa se prvi pristup PRE izbora lokala sam popravi
   * čim lokal stigne (ranije je u tom slučaju zauvek ostajao prazan).
   */
  loadUserPermissions() {
    // Jedan poziv puni oba nivoa, pa je dovoljno da je bilo koji od njih neko zatražio.
    if (!this._userPermissions && !this._organizationPermissions) {
      return;
    }

    if (!this.companyID || !this.authService.currentUserID) {
      this._userPermissions?.next([]);
      this._organizationPermissions?.next([]);
      return;
    }

    // JEDAN poziv za oba nivoa — vidi `organizationPermissions`. Stariji server taj endpoint nema,
    // pa se pada na onaj koji vraća samo prava nad lokalom; org nivo tada ostaje NEPOZNAT (`null`),
    // što pozivaoci razlikuju od „nema nijedno pravo".
    this.companyService.getMyPermissions(this.companyID).subscribe({
      next: (data) => {
        this._userPermissions?.next(data?.company ?? []);
        this._organizationPermissions?.next(data?.organization ?? []);
      },
      error: () => this.loadCompanyPermissionsOnly()
    });
  }

  private loadCompanyPermissionsOnly() {
    this._organizationPermissions?.next(null);

    this.companyService.getPermissions(this.companyID, this.authService.currentUserID).subscribe({
      next: (data) => this._userPermissions?.next(data ?? [])
    });
  }

  sendWSCompanyID(company: ICompany | null) {
    if (!company) {
      return;
    }

    const data: WebsocketData = {
      type: "setCompany",
      data: company?.companyID
    };
    this.ws.send(data);

    this.wsConnectionInitialized.next(true);
  }

  get companyID(): number {
    return this.selectedCompany.value?.companyID || 0;
  }

  onGroupSaved(s: any): void {
    var list = this.groups.value;
    this.addOrUpdate(list, s, "groupID");
    list = [...list]; // Create a new array reference to trigger change detection
    this.groups.next(list)
  }

  get groups(): BehaviorSubject<Group[]> {
    if (!this._groups) {
      this._groups = new BehaviorSubject<Group[]>([]);
      this.loadGroups();
    }

    return this._groups;
  }

  loadGroups() {
    if (!this.companyID) {
      return;
    }

    this.loadWithRetry('groups', this.groupService.get(this.companyID), {
      next: (data) => this._groups?.next(data!)
    });
  }


  /**
   * Posle snimanja podešavanja emituje se objekat koji je POSLAT (forma), a ne odgovor servera —
   * `saveSettings` vraća samo status. Taj objekat nosi samo polja koja forma uređuje, pa se spaja
   * preko zatečenog stanja umesto da ga zameni.
   *
   * Bez spajanja bi računata polja, koja se ne uređuju ni u jednoj formi, nestala do sledećeg
   * učitavanja: `organizationCssThemes` (katalog tema bi nestao, pa bi se tema skinula) i
   * `organizationCompanyCount` (portal bi se vratio na prikaz za više lokala). Server ionako radi
   * isto — `SaveSettings` popunjava učitana podešavanja poslatim poljima.
   */
  onCompanySettingsSaved(s: CompanySettings): void {
    if (this._companySettings) {
      const current = this._companySettings.value;
      this._companySettings.next(current ? { ...current, ...s } : s);
    }
  }

  /**
   * WS poruka `companySettingsChanged` (stiže posle SVAKOG snimanja, i pošiljaocu) se na serveru
   * serijalizuje bez null polja, dok ih REST `getSettings` šalje kao null. Zamena celog objekta bi
   * ta polja izbacila iz stanja: forma otvorena posle toga ih ne dobije u `patchValue`, ostane na
   * podrazumevanoj vrednosti i nju pošalje serveru (npr. `workingHours: ""` umesto null → server
   * odbija snimanje). Ključ koji u stanju postoji, a u poruci ga nema, znači da je vrednost sada
   * null — upisuje se null, ne stara vrednost, pa je stanje isto kao posle ponovnog učitavanja.
   */
  private onCompanySettingsPushed(pushed: CompanySettings): void {
    // Bez tela poruke nema šta da se upiše (ranije: poruka se upisivala kakva stigne).
    if (!pushed || !this._companySettings) {
      return;
    }

    const current = this._companySettings.value;
    if (!current) {
      this._companySettings.next(pushed);
      return;
    }

    const complete: any = { ...pushed };
    for (const key of Object.keys(current)) {
      if (!(key in complete)) {
        complete[key] = null;
      }
    }

    this._companySettings.next(complete as CompanySettings);
  }

  get companySettings() {
    if (!this._companySettings) {
      this._companySettings = new BehaviorSubject<CompanySettings | null>(null);
      this.loadCompanySettings();
    }

    return this._companySettings;
  }


  public loadCompanySettings() {
    if (!this.companyID) {
      return;
    }

    this.loadWithRetry('company settings', this.companyService.getSettings(this.companyID), {
      next: (data) => this._companySettings?.next(data)
    });
  }

  get products(): BehaviorSubject<Product[]> {
    if (!this._products) {
      this._products = new BehaviorSubject<Product[]>([]);
      this.loadProducts();
    }

    return this._products;
  }

  getSingleProduct(productID: number) {
    return this.productService.getSingleProduct(this.companyID, productID).pipe(
      tap(
        (data) => {
          if (data) {
            if (this._products?.value.some(p => p.productID !== data.productID)) {
              this._products?.next([...this._products.value, data as Product]);
            }
          }
        }
      )
    );
  }

  loadProducts() {
    if (!this.companyID) {
      return;
    }
    // Prikaz se otključava i posle neuspelog pokušaja: porudžbine se vide i dok se proizvodi još
    // traže, a nazivi iz menija se popune čim lista stigne.
    const products = this.productService.get(this.companyID).pipe(
      tap({ error: () => this.productsLoaded$.next(true) })
    );

    this.loadWithRetry('products', products, {
      next: (data) => {
        this._products?.next(data as Product[]);
        this.productsLoaded$.next(true);
      },
      // `productsLoaded$` je postavljen iznad; handler stoji da greška ne ode u globalni.
      error: () => { }
    });
  }

  moveProductToGroup(p: Product[], newGroupID: number, keepGroups: boolean): Observable<boolean> {

    var ids = p.map(pr => pr.productID);
    this.products.next([...this.products.value]);

    var result = new Subject<boolean>();

    this.productService.moveProductToGroup(this.companyID, ids, newGroupID, keepGroups).subscribe(
      success => {
        result.next(success);
        result.complete();
        if (success) {
          this.loadGroups();
        }
      }
    );

    return result;

  }

  onProductSaved(p: Product): void {
    const list = this.products.value;
    this.addOrUpdate(list, p, "productID");
    this.products.next(list)
  }

  onProductsModified() {
    this.products.next([... this.products.value]);

  }


  get priceLists(): BehaviorSubject<PriceList[]> {
    if (!this._priceLists) {
      this._priceLists = new BehaviorSubject<PriceList[]>([]);
      this.loadPriceLists();
    }

    return this._priceLists;
  }

  private loadPriceLists() {
    if (!this.companyID) {
      return;
    }

    this.priceService.get(this.companyID).subscribe({
      next: (data) => this._priceLists?.next(data as PriceList[])
    });
  }

  get modifiers(): BehaviorSubject<IModifier[]> {
    if (!this._modifiers) {
      this._modifiers = new BehaviorSubject<IModifier[]>([]);
      this.loadModifiers();
    }

    return this._modifiers;
  }

  private loadModifiers() {
    if (!this.companyID) {
      return;
    }

    this.loadWithRetry('modifiers', this.modifierService.get(this.companyID), {
      next: (data) => this._modifiers?.next(data as IModifier[])
    });
  }

  onModifierSaved(s: IModifier): void {
    var list = this.modifiers.value;
    this.addOrUpdate(list, s, "modifierID");
    list = [...list]; // Create a new array reference to trigger change detection
    this.modifiers.next(list)
  }

  onModifierDeleted(id: number): void {
    var newList = this._modifiers?.value.filter(p => p.modifierID != id);
    if (newList) {
      this._modifiers?.next(newList);
    }
  }

  addOrUpdate(list: any[], item: any, compareKey: string) {
    const existing = list.find(i => i[compareKey] == item[compareKey]);
    if (existing) {
      list.splice(list.indexOf(existing), 1, item);
    } else {
      list.push(item);
    }
  }

  remove(list: any[], item: any, compareKey: string) {
    const existing = list.find(i => i[compareKey] == item[compareKey]);
    if (existing) {
      list.splice(list.indexOf(existing), 1);
    }
  }


  getModifier(modifierID: number | null): IModifier | undefined | null {
    if (!modifierID) {
      return null;
    }
    return this.modifiers?.value?.find(m => m.modifierID == modifierID);
  }

  getProduct(productID: number): Product | undefined {
    return this.products?.value?.find(m => m.productID == productID);
  }

  getGroup(groupID: number) {
    return this.groups?.value?.find(m => m.groupID == groupID);
  }

  clear() {
    if (!environment.production) {
      this.logger.log("Clearing session");
    }
    // Učitavanja za stari lokal (i ona koja čekaju ponovni pokušaj) više nisu važna.
    this.loads.forEach(load => load.unsubscribe());
    this.loads.clear();

    this._products?.next([]);
    this._modifiers?.next([]);
    this._groups?.next([]);
    // `next([])`, NE `= null`: instanca mora da preživi promenu lokala (vidi komentar uz getter).
    this._userPermissions?.next([]);
    // Org prava se prazne u `null` („još ne znam"), ne u `[]`: prazan niz bi značio da korisnik
    // nema nijedno pravo nad firmom, pa bi mu se do sledećeg odgovora sklonili ekrani koje sme.
    this._organizationPermissions?.next(null);
    this._companySettings?.next(null);
    this._activeServiceCodes.next([]);
  }


  endSession(sessionID: string) {
    const apiAddress = environment.apiBase + 'session/end/?sessionID=' + encodeURIComponent(sessionID);

    const response = this.http.post<ApiResponse>(apiAddress, null).pipe(
      map(data => {
        if (data.success) {
          return data.success;
        }
        throw new Error(data.errorDescription);
      })
    );

    return response;
  }
}
